import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CalendarDays, List, Loader2, Plus, User } from 'lucide-react'
import { initializeTaskCNs } from 'slices/taskCNSlice'
import { initializeMyProjects } from 'slices/projectSlice'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { cn } from 'lib/utils'
import { localDayKey } from 'utils/taskTarget'
import FieldShell from '../components/FieldShell'
import FieldAvatar from '../components/FieldAvatar'
import FieldTaskComposer from '../components/FieldTaskComposer'
import FieldTaskEditSheet from '../components/FieldTaskEditSheet'

type View = 'list' | 'calendar'

const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]

/** Monday-first, because a working week is what is being planned. */
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

const startOfMonthGrid = (year: number, month: number): Date => {
	const first = new Date(year, month, 1)
	// getDay is Sunday-first; shift so Monday is 0.
	const offset = (first.getDay() + 6) % 7
	return new Date(year, month, 1 - offset)
}

/**
 * Every task the operator owns, as a list or a month.
 *
 * Both views, because they answer different questions. The list answers "what
 * is outstanding", which is what you want walking back to the car. The month
 * answers "what does next week look like", which is what you want at an
 * airport. Neither is a subset of the other, so neither is the default for
 * everyone — the choice is remembered.
 */
const FieldTasks = () => {
	const dispatch = useDispatch()
	const userId = useSelector((state: any) => state.userLogin?.userInfo?._id)
	const { entities, ids, isLoading } = useSelector((state: any) => state.taskCNs)

	const [view, setView] = useState<View>('list')
	const [composing, setComposing] = useState(false)
	/** The task whose edit sheet is open, or null. */
	const [editing, setEditing] = useState<any | null>(null)
	const [cursor, setCursor] = useState(() => new Date())
	const [selectedDay, setSelectedDay] = useState<string | null>(null)

	useEffect(() => {
		if (userId) (dispatch as any)(initializeTaskCNs({ userid: userId }))
		// The composer needs projects to file a task against, and fetching them
		// here means the form opens ready rather than spinning.
		;(dispatch as any)(initializeMyProjects())
		// And the edit sheet needs partners, to offer an assignee. Fetched here
		// for the same reason: the sheet opens ready rather than with one name
		// in the list and the rest arriving a moment later.
		;(dispatch as any)(initializeMyPartners())
	}, [dispatch, userId])

	const tasks = useMemo(
		() =>
			(ids || [])
				.map((id: string) => entities[id])
				.filter((task: any) => task && !task.isDeleted && !task.completed),
		[entities, ids],
	)

	const byDay = useMemo(() => {
		const map: Record<string, any[]> = {}
		for (const task of tasks) {
			const key = task.dateDue ? localDayKey(task.dateDue) : ''
			if (!key) continue
			;(map[key] ||= []).push(task)
		}
		return map
	}, [tasks])

	const sorted = useMemo(
		() =>
			[...tasks].sort((a, b) => {
				// Undated last: they are real tasks, but they are not the ones
				// competing for today.
				if (!a.dateDue && !b.dateDue) return 0
				if (!a.dateDue) return 1
				if (!b.dateDue) return -1
				return new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
			}),
		[tasks],
	)

	const grid = useMemo(() => {
		const start = startOfMonthGrid(cursor.getFullYear(), cursor.getMonth())
		return Array.from({ length: 42 }, (_, i) => {
			const date = new Date(start)
			date.setDate(start.getDate() + i)
			return date
		})
	}, [cursor])

	const refresh = useCallback(() => {
		if (userId) (dispatch as any)(initializeTaskCNs({ userid: userId }))
	}, [dispatch, userId])

	const todayKey = localDayKey(new Date())
	const dayTasks = selectedDay ? byDay[selectedDay] || [] : []

	return (
		<FieldShell title="Tasks" hideTabs={composing}>
			<div className="flex min-h-full flex-col">
				<div className="flex-1 pb-6">
					{/* View switch, on the same line as the count — the pattern the
					    desktop collection screens already use. */}
					<div className="flex items-center justify-between px-4 py-3">
						<p className="text-sm text-gray-500">
							{tasks.length} open {tasks.length === 1 ? 'task' : 'tasks'}
						</p>
						<div className="flex rounded-full border border-gray-300 bg-white p-0.5">
							{(['list', 'calendar'] as View[]).map(option => (
								<button
									key={option}
									type="button"
									onClick={() => setView(option)}
									aria-pressed={view === option}
									className={cn(
										'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
										view === option ? 'bg-primary text-white' : 'text-gray-600',
									)}
								>
									{option === 'list' ? (
										<List className="h-3.5 w-3.5" />
									) : (
										<CalendarDays className="h-3.5 w-3.5" />
									)}
									{option === 'list' ? 'List' : 'Calendar'}
								</button>
							))}
						</div>
					</div>

					{isLoading && tasks.length === 0 ? (
						<p className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
							<Loader2 className="h-4 w-4 animate-spin" />
							Loading tasks…
						</p>
					) : view === 'list' ? (
						<TaskList tasks={sorted} onOpen={setEditing} />
					) : (
						<>
							<div className="flex items-center justify-between px-4 pb-2">
								<button
									type="button"
									onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
									aria-label="Previous month"
									className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary"
								>
									‹
								</button>
								<p className="text-sm font-medium text-gray-800">
									{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
								</p>
								<button
									type="button"
									onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
									aria-label="Next month"
									className="inline-flex h-9 w-9 items-center justify-center rounded-full text-primary"
								>
									›
								</button>
							</div>

							<div className="grid grid-cols-7 gap-px bg-gray-200 px-3">
								{WEEKDAYS.map((day, i) => (
									<div
										key={`${day}-${i}`}
										className="bg-gray-50 py-1 text-center text-[11px] font-medium text-gray-400"
									>
										{day}
									</div>
								))}
								{grid.map(date => {
									const key = localDayKey(date)
									const count = (byDay[key] || []).length
									const otherMonth = date.getMonth() !== cursor.getMonth()
									return (
										<button
											key={key}
											type="button"
											onClick={() => setSelectedDay(count ? key : null)}
											className={cn(
												'flex min-h-[3rem] flex-col items-center justify-center gap-1 bg-white text-sm',
												otherMonth && 'text-gray-300',
												key === todayKey && 'font-bold text-primary',
												key === selectedDay && 'ring-2 ring-inset ring-primary',
											)}
										>
											{date.getDate()}
											{count > 0 && (
												<span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">
													{count}
												</span>
											)}
										</button>
									)
								})}
							</div>

							{selectedDay && (
								<div className="mt-3">
									<h2 className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
										{new Date(`${selectedDay}T12:00:00`).toLocaleDateString(undefined, {
											weekday: 'long',
											day: 'numeric',
											month: 'long',
										})}
									</h2>
									<TaskList tasks={dayTasks} onOpen={setEditing} />
								</div>
							)}
						</>
					)}
				</div>

				{composing ? (
					<FieldTaskComposer
						onDone={() => {
							setComposing(false)
							refresh()
						}}
						onCancel={() => setComposing(false)}
						className="sticky bottom-0 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
					/>
				) : (
					<div
						className="sticky bottom-0 border-t border-gray-200 bg-white p-3"
						style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
					>
						<button
							type="button"
							onClick={() => setComposing(true)}
							className="inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-white"
						>
							<Plus className="h-4 w-4" />
							Add a task
						</button>
					</div>
				)}
			</div>

			<FieldTaskEditSheet
				task={editing}
				onClose={() => setEditing(null)}
				// Refetch rather than trust the optimistic patch: completing a task
				// takes it off this list, and the list is built from the store.
				onSaved={refresh}
			/>
		</FieldShell>
	)
}

const TaskList = ({ tasks, onOpen }: { tasks: any[]; onOpen: (task: any) => void }) => {
	if (tasks.length === 0) {
		return <p className="px-4 py-6 text-sm text-gray-500">Nothing open here.</p>
	}

	return (
		<ul className="divide-y divide-gray-100 border-y border-gray-200 bg-white">
			{tasks.map(task => {
				/*
					Whose task it is now, not who typed it: assignedTo wins, and the
					creator is the fallback for a task nobody has handed on. Both
					arrive populated with name and image from /api/taskcns, so this
					costs no extra request.
				*/
				const person = task.assignedTo || task.userid
				const personName = person?.name || person?.firstName || ''

				return (
					<li key={task._id}>
						{/* The whole row opens the task: a phone target should be the
						    row, not a control tucked inside it. */}
						<button
							type="button"
							onClick={() => onOpen(task)}
							className="flex w-full items-center gap-3 px-4 py-3 text-left"
						>
							<FieldAvatar
								src={person?.image}
								name={personName}
								fallbackIcon={User}
								label={personName || 'Unassigned'}
								className="h-9 w-9"
							/>
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-medium text-gray-900">{task.taskName}</p>
								<p className="mt-0.5 truncate text-xs text-gray-500">
									{[
										task.projectid?.projectName,
										task.dateDue
											? new Date(task.dateDue).toLocaleDateString(undefined, {
													day: 'numeric',
													month: 'short',
												})
											: 'No due date',
										task.taskPriority,
									]
										.filter(Boolean)
										.join(' · ')}
								</p>
							</div>
						</button>
					</li>
				)
			})}
		</ul>
	)
}

export default FieldTasks
